import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api/apiClient';
import WindBannerLogo from '../components/WindBannerLogo';
import { FileIcon, SearchIcon, UsersIcon } from '../components/AppIcons';
import './LoginPage.css';
import { BESTEMMING } from '../components/NaarSlimmeAssistent';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  // ASA-1577: wie al een code heeft (de noodcode uit docs/runbook-noodtoegang.md), vraagt hier geen
  // nieuwe aan. Een nieuwe aanvraag maakt een nieuwere rij aan, en verifyCode leest alleen de nieuwste.
  const [alEenCode, setAlEenCode] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    const normalizedEmail = email.trim().toLowerCase();

    try {
      if (!codeSent) {
        await authApi.requestCode(normalizedEmail);
        setCodeSent(true);
        setInfo(`We hebben een code gestuurd naar ${normalizedEmail}.`);
      } else {
        const response = await authApi.verifyCode(normalizedEmail, code.trim());
        login(response.data);
        const isMobileViewport = window.matchMedia('(max-width: 900px)').matches;
        // ASA-1238/AC-1 + AC-2: BEIDE bestemmingen gaan mee. Hier stonden er twee in EEN regel -
        // een desktop- en een MOBIELE landing - en wie alleen de desktopkant aanpast, laat elke
        // telefoon op het oude pad staan. Geen router-navigatie: zie NaarSlimmeAssistent.js.
        window.location.replace(window.location.origin + BESTEMMING);
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      const fallbackMessage = err?.response?.data?.error
        ? `${err.response.data.error}${err?.response?.status ? ` (${err.response.status})` : ''}`
        : null;
      setError(serverMessage || fallbackMessage || 'Inloggen is niet gelukt. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="v-login">
      <div className="v-login__left">
        <div className="v-login__brand">
          <WindBannerLogo
            light
            subtitle="Slimme Assistent"
          />
          <h1 className="v-login__title">Ventus Slimme Assistent</h1>
          <p className="v-login__subtitle">Het interne portaal van Ventus, dat je ondersteunt in je werk.</p>
        </div>
        <div className="v-login__features">
          <div className="v-login__feature">
            <span className="v-login__feature-icon"><FileIcon /></span>
            <div>
              <strong>Alles op één plek</strong>
              <p>Wat je nodig hebt voor je werk bij Ventus, in één portaal.</p>
            </div>
          </div>
          <div className="v-login__feature">
            <span className="v-login__feature-icon"><SearchIcon /></span>
            <div>
              <strong>Slim ondersteund</strong>
              <p>Een assistent die met je meedenkt en je tijd bespaart.</p>
            </div>
          </div>
          <div className="v-login__feature">
            <span className="v-login__feature-icon"><UsersIcon /></span>
            <div>
              <strong>Alleen voor medewerkers</strong>
              <p>Je logt in met je Ventus-werkmail.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="v-login__right">
        <form className="v-login__form" onSubmit={handleSubmit}>
          <h2>Inloggen met een code</h2>
          <p className="v-login__form-sub">Zonder wachtwoord, met je werkmail.</p>

          {error && <div className="v-login__error">{error}</div>}
          {info && <div className="v-chat-empty v-chat-empty--tight">{info}</div>}

          <div className="v-form-group">
            <label>E-mailadres</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jij@ventus.nl"
              required
            />
          </div>

          {codeSent && (
            <div className="v-form-group">
              <label>Inlogcode</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6 cijfers"
                required
                minLength={6}
                maxLength={6}
              />
            </div>
          )}

          <button type="submit" className="v-btn v-btn--primary v-btn--full" disabled={loading}>
            {loading ? 'Even geduld...' : codeSent ? 'Controleer en log in' : 'Stuur een inlogcode'}
          </button>

          {!codeSent && (
            <button
              type="button"
              className="v-btn v-btn--ghost v-btn--full"
              onClick={() => {
                setError('');
                setInfo('');
                setAlEenCode(true);
                setCodeSent(true);
              }}
              disabled={loading}
            >
              Ik heb al een code
            </button>
          )}

          {alEenCode && (
            <button
              type="button"
              className="v-btn v-btn--ghost v-btn--full"
              onClick={() => {
                setError('');
                setCode('');
                setAlEenCode(false);
                setCodeSent(false);
              }}
              disabled={loading}
            >
              Terug
            </button>
          )}

          {codeSent && !alEenCode && (
            <button
              type="button"
              className="v-btn v-btn--ghost v-btn--full"
              onClick={async () => {
                setError('');
                setInfo('');
                setLoading(true);
                try {
                  await authApi.requestCode(email.trim().toLowerCase());
                  setInfo('Er is een nieuwe code gestuurd.');
                } catch (err) {
                  setError(err?.response?.data?.message || 'De code kon niet opnieuw worden verstuurd.');
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              Stuur een nieuwe code
            </button>
          )}
          {/* ASA-1286/AC-4: de testfase-regel krijgt bewust de BESTAANDE klasse
              v-login__form-sub en geen nieuwe. Een nieuwe klasse zou een CSS-bestand vragen, en
              AC-6 eist dat de diff geen CSS raakt - dat waren twee criteria die elkaar in de weg
              zaten, opgelost in de refinement (TEAMOVERLEG 06-10-2026). */}
          <p className="v-login__form-sub">Intern portaal van Ventus, nog in de testfase.</p>
        </form>
      </div>
    </div>
  );
}
